import { Type } from "class-transformer";
import i18n from "../i18n";
import { AbstractJSONModel } from "./master-model";
import { IsInt, Max, Min } from "class-validator";
import { Duty } from "./duties";
import store from "@/store";
import { eCode, EventTypes } from "./eventtypes";

export enum eEventAction {
  createEvent = 1,
  updateEvent = 2,
  deleteEvent = 3
}

export interface IEventsPost {
  id: number;
  name: string;
  event_type_id: number;
  start_time: string;
  end_time: string;
  note: string;
  employees: IEventsParticipationsPost[];
  created_by_manager: boolean;
  dates: Date[];
}

export interface IDeleteEventsOnDates {
  recurrent_event_id: number;
  dates: Date[];
}

export interface IDeleteEventsInPeriod {
  recurrent_event_id: number;
  from_date: Date;
  to_date: Date;
}

export interface IEventsParticipationsPost {
  employee_id: number;
  work_hour_pool_id: number | null;
  target: number;
}

export interface IEvents {
  id: number;
  name: string;
  event_type_id: number;
  start_time: Date;
  end_time: Date;
  note: string;
  created_at: Date;
  updated_at: Date;
  recurrent_event_id: number;
  created_by_manager: boolean;
}

export interface IPostRecurrentEvents {
  event_id: number;
  recurrent_dates: Date[];
  employee_ids: number[];
  employees: IEventsParticipationsPost[] | undefined;
}

export interface IPutRecurrentEvents {
  events: IEventsPost;
  dates: Date[] | undefined;
  from_date: Date | undefined;
  to_date: Date | undefined;
  changed_timeregistrations: IEventsParticipationsPost[];
  removed_participants: number[];
  added_participants: IEventsParticipationsPost[];
}

export class Events extends AbstractJSONModel implements IEvents {
  public id = -1;
  public name = "";
  @IsInt()
  @Min(0, { message: i18n.global.t("eventdialog.eventtypeError").toString() })
  @Max(100000)
  public event_type_id = -1;
  @Type(() => Date)
  public start_time: Date = new Date();
  @Type(() => Date)
  public end_time: Date = new Date();
  public note = "";
  @Type(() => Date)
  public created_at: Date = new Date();
  @Type(() => Date)
  public updated_at: Date = new Date();
  public recurrent_event_id = -1;
  public created_by_manager = true;

  equals(other: IEvents): boolean {
    return other.id === this.id;
  }

  //#region readonly properties
  //** */
  get isNewEvent() {
    return this.id === undefined || this.id < 0;
  }

  get isExistingEvent() {
    return !this.isNewEvent;
  }

  isCreatingSingleEvent(isRepeatedEvent: boolean) {
    return this.isNewEvent && !isRepeatedEvent;
  }

  isCreatingRecurrentEvent(isRepeatedEvent: boolean) {
    return this.isNewEvent && isRepeatedEvent;
  }

  get isModifyingSingleEvent() {
    return this.isExistingEvent && (!this.recurrent_event_id || this.recurrent_event_id === -1);
  }

  isModifyingAllRecurrentEvents(Editallrecurrentevents: boolean) {
    return this.isModifyingRecurrentEvent && Editallrecurrentevents;
  }

  isTransformingFromSingleEventToRecurrentEvent(isRepeatedEvent: boolean) {
    return this.isExistingEvent && isRepeatedEvent && !this.recurrent_event_id;
  }

  get isModifyingRecurrentEvent() {
    return this.isExistingEvent && this.recurrent_event_id && this.recurrent_event_id > -1;
  }

  async getEventDuties(): Promise<Duty[]> {
    const dutiesOnDate: Duty[] = await store.dispatch("duties/fetchDuties", {
      from: this.start_time,
      to: this.start_time,
      forceUpdate: false
    });
    return dutiesOnDate.filter((d) => d.event_id === this.id);
  }

  isClosedEvent(eventTypes: EventTypes[]) {
    return eventTypes.some(
      (et: EventTypes) => this.event_type_id == et.id && et.code !== eCode.NormalEvent
    );
  }

  //#endregion
}
